import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Program } from '../../models/application/program.interface';
import { constants } from '@edassist/common';
import { AutomatedDocumentConfigurationTableData } from '../../models/application/automated-document-configuration-table-data.interface';
import { AutomatedDocumentConfigurationDTO } from '../../models/application/automated-document-configuration-dto.interface';
import { DocumentDefaultFeature } from '../../models/program/document-default-feature.interface';
import { Client } from '../../models/application/client.interface';
import { Client as MicroClient } from '../../models/client/client.interface';
import { AddressType as ClientAddressType } from '../../models/client/address-type.interface';
import {
	ClientFeatureEducationNetwork,
	ClientFeatureSelfScheduleCoaching,
	ClientFeatures,
	ClientFeatureRefDTO,
	ClientFeaturePaymentOptions,
	UsernameType,
	ClientFeatureStudentLoanSupport,
	LoanProductType,
	ClientFeatureRepayment,
	LiveChat,
	ClientFeatureLiveChat,
	ClientFeatureTaxOptions,
	ClientFeatureApprovalDelegation,
	ClientFeaturePathways,
	ProgramFeatureTableData,
	ClientFeatureMilestones,
	MilestoneIcons,
	StepType,
	HttpParamObject,
	HttpUtilityService,
	CourseOfStudy,
	ClientEducationalProvider,
	EducationalProvider,
	SSOConfigType,
	ProductType, ClientFeatureStudentGraduation,
	FilterOptions,
	ClientFeaturePhoneSupport
} from '@edassist/api';
import { catchError, map, shareReplay } from 'rxjs/operators';
import {
	ClientFeatureSteps,
	ClientFeatureVirtualCreditCard
} from '../../models/client/client-features.interface';
import { VirtualCreditCardOptionsInterface } from '../../models/client/virtual-credit-card-options.interface';
import { withCache, CacheBucket, HttpCacheManager } from '@ngneat/cashew';
import { CareerPathway } from '../../models/career-pathway/career-pathway.interface';
import { ClientCourseOfStudy } from '../../models/application/course-of-study.interface';
import { AdminToolsForm } from '@internal-admin/app/modules/client-view/admin-tools/admin-tools-form.interface';

@Injectable({
	providedIn: 'root'
})
export class ClientService {
	private readonly relativeUrl = 'clients/';
	private readonly clientsMicroRelativeUrl = 'clients-micro/';

	// clients-micro utility data caching */
	private loanProductTypes$: Observable<Array<LoanProductType>> = undefined;
	private clientAddressTypes$: Observable<Array<ClientAddressType>> = undefined;
	private usernameTypes$: Observable<Array<UsernameType>> = undefined;
	private liveChatOptions$: Observable<Array<LiveChat>> = undefined;
	private readonly clientsBucket = new CacheBucket();
	private readonly clientFeatureStudentLoanSupportBucket = new CacheBucket();
	private readonly clientFeatureRepaymentBucket = new CacheBucket();
	private readonly clientProgramFeatureBucket = new CacheBucket();

	constructor(
		private readonly http: HttpClient,
		private readonly manager: HttpCacheManager
	) {}

	getProgramsByClientId(clientId: number): Observable<Array<Program>> {
		return this.http.get<Array<Program>>(
			`${this.relativeUrl}${clientId}/programs`
		);
	}

	getProgramsFeaturesDocumentsByClientId(
		clientId: number
	): Observable<Array<AutomatedDocumentConfigurationTableData>> {
		return this.http.get<Array<AutomatedDocumentConfigurationTableData>>(
			`${this.relativeUrl}${clientId}/programs/document-features`
		);
	}

	getDocumentTypeIdsByProgramTypeId(
		programTypeId: number
	): Observable<Array<DocumentDefaultFeature>> {
		return this.http.get<Array<DocumentDefaultFeature>>(
			`default-documents/programTypes/${programTypeId}`
		);
	}

	saveDocumentConfig(
		automatedDocumentConfigurationDTO: AutomatedDocumentConfigurationDTO
	): Observable<AutomatedDocumentConfigurationDTO> {
		return this.http.post<AutomatedDocumentConfigurationDTO>(
			`programs/${automatedDocumentConfigurationDTO.programId}/features/document`,
			automatedDocumentConfigurationDTO
		);
	}

	isClientProgramFeatureEnabled(
		clientId: number,
		programFeatureCode: string,
		refresh?: boolean
	): Observable<boolean> {
		if (refresh) {
			this.manager.delete(this.clientProgramFeatureBucket);
		}

		return this.http
			.get<boolean>(`${this.relativeUrl}${clientId}/programFeatureEnabled`, {
				params: { programFeatureCode },
				context: withCache({
					key: `isClientProgramFeatureEnabled-${clientId}-${programFeatureCode}`,
					bucket: this.clientProgramFeatureBucket,
					version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1
				})
			})
			.pipe(catchError(() => of(false)));
	}

	getClientByApplicationId(applicationId: number): Observable<Client> {
		return this.http.get<Client>(
			`${this.relativeUrl}by-application${applicationId}`
		);
	}

	// clients-micro endpoints */
	/** Fetches available options for MicroClient's [mailing/email]AddressTypeId */
	getClientAddressTypes(
		refresh?: boolean
	): Observable<Array<ClientAddressType>> {
		if (!this.clientAddressTypes$ || refresh) {
			this.clientAddressTypes$ = this.http
				.get<
					Array<ClientAddressType>
				>(`${this.clientsMicroRelativeUrl}util/address-types`)
				.pipe(shareReplay(1));
		}

		return this.clientAddressTypes$;
	}

	/** Fetches available options for MicroClient's userNameTypeId */
	getUsernameTypes(refresh?: boolean): Observable<Array<UsernameType>> {
		if (!this.usernameTypes$ || refresh) {
			this.usernameTypes$ = this.http
				.get<
					Array<UsernameType>
				>(`${this.clientsMicroRelativeUrl}util/username-types`)
				.pipe(shareReplay(1));
		}

		return this.usernameTypes$;
	}

	/** Creates a new client */
	createClient(clientName: string): Observable<MicroClient> {
		const payload: MicroClient = {
			name: clientName,
			userNameTypeId: 'EmployeeId'
		};

		return this.http.post<MicroClient>(
			`${this.clientsMicroRelativeUrl}`,
			payload
		);
	}

	/** Fetches all clients */
	getClients(activeOnly = true): Observable<Array<MicroClient>> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams({
			activeOnly
		});

		return this.http.get<Array<MicroClient>>(
			`${this.clientsMicroRelativeUrl}`,
			queryParams
		);
	}

	/** Fetches client by ID */
	getClientById(clientId: number): Observable<MicroClient> {
		return this.http
			.get<MicroClient>(`${this.clientsMicroRelativeUrl}${clientId}`)
			.pipe(catchError(() => of(undefined)));
	}

	/** Updates client by ID */
	updateClientById(
		clientId: number,
		payload: MicroClient
	): Observable<MicroClient> {
		return this.http.put<MicroClient>(
			`${this.clientsMicroRelativeUrl}${clientId}`,
			payload
		);
	}

	/** Gets all features for a specific client by ID */
	getClientFeaturesById(clientId: number): Observable<ClientFeatures> {
		return this.http
			.get<
				Array<ClientFeatureRefDTO>
			>(`${this.clientsMicroRelativeUrl}${clientId}/features`)
			.pipe(
				map((response) => {
					const clientFeatures: ClientFeatures = { clientId };
					response.forEach((feat) => {
						clientFeatures[feat.clientFeatureId] = {
							id: feat.id
						};
					});

					return clientFeatures;
				}),
				// tslint:disable-next-line:no-unnecessary-callback-wrapper
				catchError((error) => of(error))
			);
	}

	/** Filter ClientFeaturesById with featureCode and return boolean */
	isClientFeatureEnabled(
		clientId: number,
		featureCode: string
	): Observable<boolean> {
		return this.getClientFeaturesById(clientId).pipe(
			map((response) => !!response[featureCode])
		);
	}

	/** Turn on features for a specific client by ID */
	addClientFeaturesById(
		clientId: number,
		features: Array<string>
	): Observable<void> {
		const payload: Array<ClientFeatureRefDTO> = features.map((feat) => ({
			clientId,
			clientFeatureId: feat
		}));

		return this.http.post<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features`,
			payload
		);
	}

	/** Turn off list of features for a specific client by ID */
	deleteClientFeaturesById(
		clientId: number,
		featureIds: Array<number>
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/${featureIds}`
		);
	}

	// Edassist Education Network */
	/** Get client edassist education network data */
	getClientFeatureEdassistEducationNetwork(
		clientId: number
	): Observable<ClientFeatureEducationNetwork> {
		return this.http.get<ClientFeatureEducationNetwork>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/education-network`
		);
	}

	/** Add client edassist education network data */
	addClientFeatureEdassistEducationNetwork(
		clientId: number,
		payload: ClientFeatureEducationNetwork
	): Observable<ClientFeatureEducationNetwork> {
		return this.http.post<ClientFeatureEducationNetwork>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/education-network`,
			payload
		);
	}

	/** Update client edassist education network data */
	updateClientFeatureEdassistEducationNetwork(
		clientId: number,
		featureId: number,
		payload: ClientFeatureEducationNetwork
	): Observable<ClientFeatureEducationNetwork> {
		return this.http.put<ClientFeatureEducationNetwork>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/education-network/${featureId}`,
			payload
		);
	}

	/** Delete client edassist education network data, called when education network is turned off */
	deleteClientFeatureEdassistEducationNetwork(
		clientId: number,
		featureId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/education-network/${featureId}`
		);
	}

	// Self-schedule Coaching */
	/** Get client self-schedule coaching data */
	getClientFeatureSelfScheduleCoaching(
		clientId: number
	): Observable<ClientFeatureSelfScheduleCoaching> {
		return this.http.get<ClientFeatureSelfScheduleCoaching>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/self-schedule-coaching`
		);
	}

	/** Add client self-schedule coaching data */
	addClientFeatureSelfScheduleCoaching(
		clientId: number,
		payload: ClientFeatureSelfScheduleCoaching
	): Observable<ClientFeatureSelfScheduleCoaching> {
		return this.http.post<ClientFeatureSelfScheduleCoaching>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/self-schedule-coaching`,
			payload
		);
	}

	/** Update client self-schedule coaching data */
	updateClientFeatureSelfScheduleCoaching(
		clientId: number,
		featureId: number,
		payload: ClientFeatureSelfScheduleCoaching
	): Observable<ClientFeatureSelfScheduleCoaching> {
		return this.http.put<ClientFeatureSelfScheduleCoaching>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/self-schedule-coaching/${featureId}`,
			payload
		);
	}

	/** Delete client self-schedule coaching, called when self-schedule coaching is turned off */
	deleteClientFeatureSelfScheduleCoaching(
		clientId: number,
		featureId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/self-schedule-coaching/${featureId}`
		);
	}

	// Student Loan Support */
	/** Get client student loan support data */
	getClientFeatureStudentLoanSupport(
		clientId: number,
		refresh?: boolean
	): Observable<ClientFeatureStudentLoanSupport> {
		if (refresh) {
			this.manager.delete(this.clientFeatureStudentLoanSupportBucket);
		}

		return this.http.get<ClientFeatureStudentLoanSupport>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/student-loan-support`,
			{
				context: withCache({
					bucket: this.clientFeatureStudentLoanSupportBucket
				})
			}
		);
	}

	/** Add client student loan support data */
	addClientFeatureStudentLoanSupport(
		clientId: number,
		payload: ClientFeatureStudentLoanSupport
	): Observable<ClientFeatureStudentLoanSupport> {
		return this.http.post<ClientFeatureStudentLoanSupport>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/student-loan-support`,
			payload
		);
	}

	/** Update client student loan support data */
	updateClientFeatureStudentLoanSupport(
		clientId: number,
		featureId: number,
		payload: ClientFeatureStudentLoanSupport
	): Observable<ClientFeatureStudentLoanSupport> {
		return this.http.put<ClientFeatureStudentLoanSupport>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/student-loan-support/${featureId}`,
			payload
		);
	}

	/** Delete client student loan support, called when student loan support is turned off */
	deleteClientFeatureStudentLoanSupport(
		clientId: number,
		featureId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/student-loan-support/${featureId}`
		);
	}

	/** Fetches available options for MicroClient's loan product types */
	getLoanProductTypes(refresh?: boolean): Observable<Array<LoanProductType>> {
		if (!this.loanProductTypes$ || refresh) {
			this.loanProductTypes$ = this.http
				.get<
					Array<LoanProductType>
				>(`${this.clientsMicroRelativeUrl}util/loan-product-types`)
				.pipe(shareReplay(1));
		}

		return this.loanProductTypes$;
	}

	getProductTypes(
		clientId: number,
		participantId: number
	): Observable<Array<ProductType>> {
		return this.http.get<Array<ProductType>>(
			`${this.relativeUrl}${clientId}/participants/${participantId}/product-types`
		);
	}

	// Payment Options */
	/** Get client payment options data */
	getClientFeaturePaymentOptions(
		clientId: number
	): Observable<ClientFeaturePaymentOptions> {
		return this.http.get<ClientFeaturePaymentOptions>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/payment-options`
		);
	}

	/** Update client payment options data */
	updateClientFeaturePaymentOptions(
		clientId: number,
		featureId: number,
		payload: ClientFeaturePaymentOptions
	): Observable<ClientFeaturePaymentOptions> {
		return this.http.put<ClientFeaturePaymentOptions>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/payment-options/${featureId}`,
			payload
		);
	}

	// Repayment */
	/** Get client repayment data */
	getClientFeatureRepayment(
		clientId: number
	): Observable<ClientFeatureRepayment> {
		return this.http.get<ClientFeatureRepayment>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/repayment`,
			{
				context: withCache({
					bucket: this.clientFeatureRepaymentBucket
				})
			}
		);
	}

	/** Add client repayment data */
	addClientFeatureRepayment(
		clientId: number,
		payload: ClientFeatureRepayment
	): Observable<ClientFeatureRepayment> {
		this.manager.delete(this.clientFeatureRepaymentBucket);

		return this.http.post<ClientFeatureRepayment>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/repayment`,
			payload
		);
	}

	/** Update client repayment data */
	updateClientFeatureRepayment(
		clientId: number,
		featureId: number,
		payload: ClientFeatureRepayment
	): Observable<ClientFeatureRepayment> {
		this.manager.delete(this.clientFeatureRepaymentBucket);

		return this.http.put<ClientFeatureRepayment>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/repayment/${featureId}`,
			payload
		);
	}

	/** Delete client repayment, called when repayment is turned off */
	deleteClientFeatureRepayment(
		clientId: number,
		featureId: number
	): Observable<void> {
		this.manager.delete(this.clientFeatureRepaymentBucket);

		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/repayment/${featureId}`
		);
	}

	// Support Services - Live Chat */
	/** Get client live chat data */
	getClientFeatureLiveChat(
		clientId: number
	): Observable<ClientFeatureLiveChat> {
		return this.http.get<ClientFeatureLiveChat>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/live-chat`
		);
	}

	getClientFeaturePhoneSupport(
		clientId: number
	): Observable<ClientFeaturePhoneSupport> {
		return this.http.get<ClientFeaturePhoneSupport>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/phone-support-feature`
		);
	}

	addClientFeaturePhoneSupport(
		clientId: number,
		payload: ClientFeaturePhoneSupport
	): Observable<ClientFeaturePhoneSupport> {
		return this.http.post<ClientFeaturePhoneSupport>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/phone-support-feature`,
			payload
		);
	}

	updateClientFeaturePhoneSupport(
		clientId: number,
		featureId: number,
		payload: ClientFeaturePhoneSupport
	): Observable<ClientFeaturePhoneSupport> {
		return this.http.put<ClientFeaturePhoneSupport>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/phone-support-feature/${featureId}`,
			payload
		);
	}

	deleteClientFeaturePhoneSupport(
		clientId: number,
		featureId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/phone-support-feature/${featureId}`
		);
	}

	/** Add client live chat data */
	addClientFeatureLiveChat(
		clientId: number,
		payload: ClientFeatureLiveChat
	): Observable<ClientFeatureLiveChat> {
		return this.http.post<ClientFeatureLiveChat>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/live-chat`,
			payload
		);
	}

	/** Update client live chat data */
	updateClientFeatureLiveChat(
		clientId: number,
		featureId: number,
		payload: ClientFeatureLiveChat
	): Observable<ClientFeatureLiveChat> {
		return this.http.put<ClientFeatureLiveChat>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/live-chat/${featureId}`,
			payload
		);
	}

	/** Delete client live chat, called when live chat is turned off */
	deleteClientFeatureLiveChat(
		clientId: number,
		featureId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/live-chat/${featureId}`
		);
	}

	/** Fetches live chat options for client feature */
	getLiveChatConfigs(refresh?: boolean): Observable<Array<LiveChat>> {
		if (!this.liveChatOptions$ || refresh) {
			this.liveChatOptions$ = this.http
				.get<
					Array<LiveChat>
				>(`${this.clientsMicroRelativeUrl}live-chat-configs`)
				.pipe(shareReplay(1));
		}

		return this.liveChatOptions$;
	}

	getClientFeatureTaxOptions(
		clientId: number
	): Observable<ClientFeatureTaxOptions> {
		return this.http.get<ClientFeatureTaxOptions>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/taxes`
		);
	}

	updateClientFeatureTaxOptions(
		clientId: number,
		featureId: number,
		payload: ClientFeatureTaxOptions
	): Observable<ClientFeatureTaxOptions> {
		return this.http.put<ClientFeatureTaxOptions>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/taxes/${featureId}`,
			payload
		);
	}

	getClientFeatureApprovalDelegation(
		clientId: number
	): Observable<ClientFeatureApprovalDelegation> {
		return this.http.get<ClientFeatureApprovalDelegation>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/approval-delegation`
		);
	}

	postClientFeatureApprovalDelegation(
		clientId: number,
		payload: ClientFeatureApprovalDelegation
	): Observable<ClientFeatureApprovalDelegation> {
		return this.http.post<ClientFeatureApprovalDelegation>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/approval-delegation`,
			payload
		);
	}

	updateClientFeatureApprovalDelegation(
		clientId: number,
		featureId: number,
		payload: ClientFeatureApprovalDelegation
	): Observable<ClientFeatureApprovalDelegation> {
		return this.http.put<ClientFeatureApprovalDelegation>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/approval-delegation/${featureId}`,
			payload
		);
	}

	deleteClientFeatureApprovalDelegation(
		clientId: number,
		featureId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/approval-delegation/${featureId}`
		);
	}

	getClientProgramFeatures(
		clientId: number
	): Observable<Array<ProgramFeatureTableData>> {
		return this.http.get<Array<ProgramFeatureTableData>>(
			`${this.relativeUrl}${clientId}/program-features`
		);
	}

	postClientFeatureVirtualCreditCard(
		clientId: number,
		payload: ClientFeatureVirtualCreditCard
	): Observable<ClientFeatureVirtualCreditCard> {
		return this.http.post<ClientFeatureVirtualCreditCard>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/virtual-credit-card`,
			payload
		);
	}

	getClientFeatureVirtualCreditCard(
		clientId: number
	): Observable<ClientFeatureVirtualCreditCard> {
		return this.http.get<ClientFeatureVirtualCreditCard>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/virtual-credit-card`
		);
	}

	updateClientFeatureVirtualCreditCard(
		clientId: number,
		featureId: number,
		payload: ClientFeatureVirtualCreditCard
	): Observable<ClientFeatureVirtualCreditCard> {
		return this.http.put<ClientFeatureVirtualCreditCard>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/virtual-credit-card/${featureId}`,
			payload
		);
	}

	deleteClientFeatureVirtualCreditCard(
		clientId: number,
		featureId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/virtual-credit-card/${featureId}`
		);
	}

	getClientFeatureVirtualCreditCardFields(): Observable<
		Array<VirtualCreditCardOptionsInterface>
	> {
		return this.http.get<Array<VirtualCreditCardOptionsInterface>>(
			`${this.clientsMicroRelativeUrl}virtual-credit-card/field-options`
		);
	}

	getClientFeatureEducationPathways(
		clientId: number
	): Observable<Array<ClientFeaturePathways>> {
		return this.http.get<Array<ClientFeaturePathways>>(
			`${this.relativeUrl}${clientId}/features/education-pathways`
		);
	}

	getClientFeatureEducationPathway(
		clientId: number,
		pathwaysId: number
	): Observable<ClientFeaturePathways> {
		return this.http.get<ClientFeaturePathways>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${pathwaysId}`
		);
	}

	postClientFeatureEducationPathways(
		clientId: number,
		payload: ClientFeaturePathways
	): Observable<ClientFeaturePathways> {
		return this.http.post<ClientFeaturePathways>(
			`${this.relativeUrl}${clientId}/features/education-pathway`,
			payload
		);
	}

	updateClientFeatureEducationPathway(
		clientId: number,
		pathwaysId: number,
		payload: ClientFeaturePathways
	): Observable<ClientFeaturePathways> {
		return this.http.put<ClientFeaturePathways>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${pathwaysId}`,
			payload
		);
	}

	deleteClientFeatureEducationPathway(
		clientId: number,
		pathwaysId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${pathwaysId}`
		);
	}

	postClientFeatureEducationMilestone(
		clientId: number,
		featureId: number,
		payload: ClientFeatureMilestones
	): Observable<ClientFeatureMilestones> {
		return this.http.post<ClientFeatureMilestones>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${featureId}/milestones`,
			payload
		);
	}

	updateClientFeatureEducationMilestone(
		clientId: number,
		featureId: number,
		milestoneId: number,
		payload: ClientFeatureMilestones
	): Observable<ClientFeatureMilestones> {
		return this.http.put<ClientFeatureMilestones>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${featureId}/milestones/${milestoneId}`,
			payload
		);
	}

	getClientFeatureEducationMilestoneIcons(): Observable<Array<MilestoneIcons>> {
		return this.http.get<Array<MilestoneIcons>>(
			'features/education-pathways/milestone-icons'
		);
	}

	getClientFeatureEducationMilestones(
		clientId: number,
		featureId: number
	): Observable<Array<ClientFeatureMilestones>> {
		return this.http.get<Array<ClientFeatureMilestones>>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${featureId}/milestones`
		);
	}

	deleteClientFeatureEducationMilestones(
		clientId: number,
		pathwayId: number,
		milestoneId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${pathwayId}/milestones/${milestoneId}`
		);
	}

	postClientFeatureEducationStep(
		clientId: number,
		pathwayId: number,
		milestoneId: number,
		payload: ClientFeatureSteps
	): Observable<ClientFeatureSteps> {
		return this.http.post<ClientFeatureSteps>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${pathwayId}/milestones/${milestoneId}/steps`,
			payload
		);
	}

	updateClientFeatureEducationStep(
		clientId: number,
		pathwayId: number,
		milestoneId: number,
		stepId: number,
		payload: ClientFeatureSteps
	): Observable<ClientFeatureSteps> {
		return this.http.put<ClientFeatureSteps>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${pathwayId}/milestones/` +
				`${milestoneId}/steps/${stepId}`,
			payload
		);
	}

	getClientFeatureEducationSteps(
		clientId: number,
		pathwayId: number,
		milestoneId: number
	): Observable<Array<ClientFeatureSteps>> {
		return this.http.get<Array<ClientFeatureSteps>>(
			`${this.relativeUrl}${clientId}/features/education-pathways/` +
				`${pathwayId}/milestones/${milestoneId}/steps`
		);
	}

	deleteClientFeatureEducationStep(
		clientId: number,
		pathwayId: number,
		milestoneId: number,
		stepId: number
	): Observable<void> {
		return this.http.delete<void>(
			`${this.relativeUrl}${clientId}/features/education-pathways/${pathwayId}/milestones/${milestoneId}/steps/${stepId}`
		);
	}

	getClientFeatureEducationStepTypes(): Observable<Array<StepType>> {
		return this.http.get<Array<StepType>>('education-pathways/step-types');
	}

	getRelatedClients(clientId: number): Observable<Array<MicroClient>> {
		return this.http
			.get<
				Array<MicroClient>
			>(`${this.clientsMicroRelativeUrl}${clientId}/related-clients`)
			.pipe(catchError(() => of([])));
	}

	getCareerPathways(clientId: number): Observable<Array<CareerPathway>> {
		return this.http.get<Array<CareerPathway>>(
			`${this.relativeUrl}${clientId}/features/career-pathways`
		);
	}

	updateCareerPathway(
		clientId: number,
		updatedCareerPathway: CareerPathway
	): Observable<CareerPathway> {
		return this.http.put<CareerPathway>(
			`${this.relativeUrl}${clientId}/features/career-pathways/${updatedCareerPathway.id}`,
			updatedCareerPathway
		);
	}

	saveCareerPathway(
		clientId: number,
		careerPathway: CareerPathway
	): Observable<CareerPathway> {
		return this.http.post<CareerPathway>(
			`${this.relativeUrl}${clientId}/features/career-pathways`,
			careerPathway
		);
	}

	deleteCareerPathway(clientId: number, pathwayId: number): Observable<void> {
		return this.http.delete<void>(
			`${this.relativeUrl}${clientId}/features/career-pathways/${pathwayId}`
		);
	}

	getCoursesOfStudy(clientId: number): Observable<Array<CourseOfStudy>> {
		return this.http.get<Array<CourseOfStudy>>(
			`${this.relativeUrl}${clientId}/courses-of-study`
		);
	}

	getSelectedCoursesOfStudy(
		clientId: number
	): Observable<Array<ClientCourseOfStudy>> {
		return this.http.get<Array<ClientCourseOfStudy>>(
			`pathways/clients/${clientId}/courses-of-study`
		);
	}

	saveSelectedCoursesOfStudy(
		clientId: number,
		data: Array<ClientCourseOfStudy>
	): Observable<Array<ClientCourseOfStudy>> {
		return this.http.post<Array<ClientCourseOfStudy>>(
			`pathways/clients/${clientId}/courses-of-study`,
			data
		);
	}

	saveSelectedEducationProviders(
		clientId: number,
		data: Array<ClientEducationalProvider>
	): Observable<Array<ClientEducationalProvider>> {
		return this.http.post<Array<ClientEducationalProvider>>(
			`pathways/clients/${clientId}/educational-providers`,
			data
		);
	}

	getSelectedEducationProviders(
		clientId: number
	): Observable<Array<EducationalProvider>> {
		return this.http.get<Array<EducationalProvider>>(
			`pathways/clients/${clientId}/educational-providers`
		);
	}

	deleteEducationProvider(
		clientId: number,
		providerId: number
	): Observable<void> {
		return this.http.delete<void>(
			`pathways/clients/${clientId}/educational-providers/${providerId}`
		);
	}

	getSSOFields(clientId: number): Observable<SSOConfigType> {
		return this.http.get<SSOConfigType>(
			`${this.clientsMicroRelativeUrl}${clientId}/sso-fields`
		);
	}

	postSSOFields(
		clientId: number,
		ssoFields: AdminToolsForm
	): Observable<SSOConfigType> {
		return this.http.post<SSOConfigType>(
			`${this.clientsMicroRelativeUrl}${clientId}/sso-fields`,
			ssoFields
		);
	}

	putSSOFields(
		clientId: number,
		ssoId: number,
		ssoFields: AdminToolsForm
	): Observable<SSOConfigType> {
		return this.http.put<SSOConfigType>(
			`${this.clientsMicroRelativeUrl}${clientId}/sso-fields/${ssoId}`,
			ssoFields
		);
	}

	getStudentGraduationFeature(clientId: number) : Observable<ClientFeatureStudentGraduation> {
		return this.http.get<ClientFeatureStudentGraduation>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/student-graduation`
		);
	}

	addStudentGraduationFeature(studentGraduationFeature: ClientFeatureStudentGraduation): Observable<void> {
		return this.http.post<void>(
			`${this.clientsMicroRelativeUrl}${studentGraduationFeature.clientId}/features/student-graduation`,
			studentGraduationFeature
		);
	}
	updateStudentGraduationFeature(studentGraduationFeature: ClientFeatureStudentGraduation): Observable<void> {
		return this.http.put<void>(
			`${this.clientsMicroRelativeUrl}${studentGraduationFeature.clientId}/features/student-graduation/${studentGraduationFeature.id}`,
			studentGraduationFeature
		);
	}

	deleteStudentGraduationFeature(clientId: number, studentGraduationId: number): Observable<void> {
		return this.http.delete<void>(
			`${this.clientsMicroRelativeUrl}${clientId}/features/student-graduation/${studentGraduationId}`
		);
	}

	getTaxYearOptions(clientId: number): Observable<Array<FilterOptions>> {

		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${clientId}/tax-year-options`
		);
	}
}
